import { RectangleDashedIcon } from '@phosphor-icons/react';
import {
  Cell,
  Column,
  EmptyState,
  Pagination,
  Row,
  Table,
  TableBody,
  TableContainer,
  TableHeader,
} from '@skupreme/shared/ui';
import type { ConflictItem } from '../lib/types';
import { ConflictRow, type ConflictRowProps } from './ConflictRow';

export type ConflictsTableProps = Omit<ConflictRowProps, 'item' | 'region'> & {
  items: ConflictItem[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalData: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Region names by id, for the flag. */
  regionNames: Record<string, string>;
};

const LOADING_ROWS = 7;
const COLUMN_COUNT = 5;

/** Catalog items with attributes left to fill, each resolved from its row. */
export function ConflictsTable({
  items,
  loading,
  page,
  pageSize,
  totalData,
  onPageChange,
  onPageSizeChange,
  regionNames,
  ...rowProps
}: ConflictsTableProps) {
  return (
    <TableContainer className="sk:overflow-visible sk:rounded-card sk:bg-surface">
      <Table aria-label="Missing information" className="sk:table-fixed">
        <TableHeader>
          <Column id="product" isRowHeader className="sk:w-[30%]">
            Product
          </Column>
          <Column id="missing" className="sk:w-[30%]">
            Missing information
          </Column>
          <Column id="marketplaces" className="sk:w-[16%]">
            Marketplaces
          </Column>
          <Column id="region" className="sk:w-[8%]">
            Region
          </Column>
          <Column id="actions" aria-label="Actions" className="sk:w-[16%]">
            {''}
          </Column>
        </TableHeader>
        <TableBody
          renderEmptyState={() => (
            <EmptyState
              icon={<RectangleDashedIcon size={20} />}
              title="No missing information"
              description="Every catalog item in this view has what it needs."
            />
          )}
        >
          {loading
            ? Array.from({ length: LOADING_ROWS }, (_, row) => (
                <Row key={`placeholder-${row}`} id={`placeholder-${row}`}>
                  <Cell colSpan={COLUMN_COUNT}>
                    <div className="sk:h-10 sk:animate-pulse sk:rounded-option sk:bg-fill-tertiary" />
                  </Cell>
                </Row>
              ))
            : items.map(item => (
                <ConflictRow
                  key={item._id}
                  item={item}
                  region={
                    item.marketplace_region_id
                      ? regionNames[item.marketplace_region_id]
                      : undefined
                  }
                  {...rowProps}
                />
              ))}
        </TableBody>
      </Table>
      {totalData > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={totalData}
          onPageChange={nextPage => {
            onPageChange(nextPage);
            window.scrollTo({ top: 0 });
          }}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </TableContainer>
  );
}
